import * as regex from '../constants/Regex'
import {PREFIJOS_CODIGO_ESTABLECIMIENTO_V2} from './normativa.utils'

export const LONGITUD_SERIE = 8

export const LONGITUD_CODIGO_ESTABLECIMIENTO = 4

export const LONGITUD_PUNTO_VENTA = 4

export const EJEMPLO_SERIE_V2 = 'M001P025'

export const EJEMPLO_CODIGO_ESTABLECIMIENTO_V2 = 'M001'

export const EJEMPLO_PUNTO_VENTA_V2 = 'P001'

export const normalizarSerie = (valor: string): string => `${valor ?? ''}`.trim().toUpperCase()

export const validarSerieDocumentoTecleo = (valor: string): boolean =>
  regex.REGEX_SERIE_DOCUMENTO_TECLEO.test(valor)

export const validarSerieDocumentoV1 = (valor: string): boolean =>
  regex.REGEX_SERIE_DOCUMENTO_V1.test(normalizarSerie(valor))

export const validarSerieDocumentoV2 = (valor: string): boolean =>
  regex.REGEX_SERIE_DOCUMENTO_V2.test(normalizarSerie(valor))

export const validarCodigoEstablecimientoV2 = (valor: string): boolean =>
  regex.REGEX_CODIGO_ESTABLECIMIENTO_V2.test(normalizarSerie(valor))

export const validarPuntoVentaV2 = (valor: string): boolean =>
  regex.REGEX_PUNTO_VENTA_V2.test(normalizarSerie(valor))

export const partirSerie = (valor: string) => {
  const serie = normalizarSerie(valor)
  return {
    codigoEstablecimiento: serie.substring(0, 4),
    puntoVenta: serie.substring(4, LONGITUD_SERIE),
  }
}

export const describirPrefijoCodigoEstablecimiento = (serie: string): string => {
  const letra = normalizarSerie(serie).charAt(0)
  const tipo = PREFIJOS_CODIGO_ESTABLECIMIENTO_V2.find((item) => item.letra === letra)
  return tipo ? tipo.descripcion : ''
}

const esDigito = (caracter: string): boolean => caracter >= '0' && caracter <= '9'

const todosDigitos = (valor: string): boolean => {
  if (!valor) return false
  for (let i = 0; i < valor.length; i += 1) {
    if (!esDigito(valor.charAt(i))) return false
  }
  return true
}

export const diagnosticarSerieV2 = (valor: string): string => {
  const serie = normalizarSerie(valor)

  if (!serie) return `Escriba la serie del documento. El formato es ${EJEMPLO_SERIE_V2}.`

  if (!regex.REGEX_SERIE_DOCUMENTO_TECLEO.test(serie)) {
    return 'La serie solo admite letras y números: quite espacios, guiones y cualquier otro signo.'
  }

  if (serie.length !== LONGITUD_SERIE) {
    return `La serie debe tener ${LONGITUD_SERIE} caracteres y la suya tiene ${serie.length}. El formato es ${EJEMPLO_SERIE_V2}.`
  }

  const letraEstablecimiento = serie.charAt(0)
  if (!PREFIJOS_CODIGO_ESTABLECIMIENTO_V2.some((tipo) => tipo.letra === letraEstablecimiento)) {
    return `El primer carácter debe ser M (casa matriz), B (bodega), S (sucursal) o P (patio); usted escribió "${letraEstablecimiento}".`
  }

  if (!todosDigitos(serie.substring(1, 4))) {
    return `Los caracteres 2, 3 y 4 son el número del establecimiento y deben ser tres dígitos. Ejemplo: ${EJEMPLO_SERIE_V2}.`
  }

  if (serie.charAt(4) !== 'P') {
    return `El quinto carácter debe ser la letra P, que antecede al punto de venta; usted escribió "${serie.charAt(
      4
    )}".`
  }

  if (!todosDigitos(serie.substring(5, LONGITUD_SERIE))) {
    return `Los últimos tres caracteres son el número del punto de venta y deben ser tres dígitos. Ejemplo: ${EJEMPLO_SERIE_V2}.`
  }

  return ''
}

export const unirSerie = (codigoEstablecimiento: string, puntoVenta: string): string =>
  `${normalizarSerie(codigoEstablecimiento)}${normalizarSerie(puntoVenta)}`

export const diagnosticarCodigoEstablecimientoV2 = (valor: string): string => {
  const codigo = normalizarSerie(valor)

  if (!codigo) {
    return `Escriba el código de la sucursal. El formato es ${EJEMPLO_CODIGO_ESTABLECIMIENTO_V2}.`
  }

  if (!validarSerieDocumentoTecleo(codigo)) {
    return 'El código de la sucursal solo admite letras y números: quite espacios, guiones y cualquier otro signo.'
  }

  if (codigo.length !== LONGITUD_CODIGO_ESTABLECIMIENTO) {
    return `El código de la sucursal debe tener ${LONGITUD_CODIGO_ESTABLECIMIENTO} caracteres y el suyo tiene ${codigo.length}. El formato es ${EJEMPLO_CODIGO_ESTABLECIMIENTO_V2}.`
  }

  const letraEstablecimiento = codigo.charAt(0)
  if (!PREFIJOS_CODIGO_ESTABLECIMIENTO_V2.some((tipo) => tipo.letra === letraEstablecimiento)) {
    return `El primer carácter debe ser M (casa matriz), B (bodega), S (sucursal) o P (patio); usted escribió "${letraEstablecimiento}".`
  }

  if (!todosDigitos(codigo.substring(1, LONGITUD_CODIGO_ESTABLECIMIENTO))) {
    return `Los últimos tres caracteres del código son el número del establecimiento y deben ser dígitos. Ejemplo: ${EJEMPLO_CODIGO_ESTABLECIMIENTO_V2}.`
  }

  return ''
}

export const diagnosticarPuntoVentaV2 = (valor: string): string => {
  const puntoVenta = normalizarSerie(valor)

  if (!puntoVenta) {
    return `Escriba el número de la sucursal. El formato es ${EJEMPLO_PUNTO_VENTA_V2}.`
  }

  if (!validarSerieDocumentoTecleo(puntoVenta)) {
    return 'El número de la sucursal solo admite letras y números: quite espacios, guiones y cualquier otro signo.'
  }

  if (puntoVenta.length !== LONGITUD_PUNTO_VENTA) {
    return `El número de la sucursal debe tener ${LONGITUD_PUNTO_VENTA} caracteres y el suyo tiene ${puntoVenta.length}. El formato es ${EJEMPLO_PUNTO_VENTA_V2}.`
  }

  if (puntoVenta.charAt(0) !== 'P') {
    return `El primer carácter debe ser la letra P, que antecede al punto de venta; usted escribió "${puntoVenta.charAt(
      0
    )}".`
  }

  if (!todosDigitos(puntoVenta.substring(1, LONGITUD_PUNTO_VENTA))) {
    return `Los últimos tres caracteres son el número del punto de venta y deben ser dígitos. Ejemplo: ${EJEMPLO_PUNTO_VENTA_V2}.`
  }

  return ''
}

export const describirSerie = (valor: string): string => {
  if (!validarSerieDocumentoV2(valor)) return ''
  const {codigoEstablecimiento, puntoVenta} = partirSerie(valor)
  const tipo = describirPrefijoCodigoEstablecimiento(valor)
  const numeroEstablecimiento = codigoEstablecimiento.substring(1, 4)
  const numeroPuntoVenta = puntoVenta.substring(1, 4)
  return `${tipo} ${numeroEstablecimiento}, punto de venta ${numeroPuntoVenta}.`
}
